import TextField from '@mui/material/TextField';
import { useState } from 'react';
import type {
  ControllerRenderProps,
  FieldValues,
  Path,
  UseFormTrigger,
} from 'react-hook-form';

import Icon, { IconName } from '@/components/Icon/Icon';

type DropdownTextFieldProps<T extends FieldValues> = {
  field?: ControllerRenderProps<T, Path<T>>;
  trigger?: UseFormTrigger<T>;
  disabled?: boolean;
  selectOptions: React.JSX.Element[];
  option: string;
  labelId: string;
  handleChange: (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => void;
  placeholder?: string;
  error?: string;
  classes: {
    base: string;
    placeholder: string;
    menu: string;
  };
  disablePortal?: boolean;
  menuPaperClassName?: string;
  icon?: IconName;
};

const DropdownTextField = <T extends FieldValues>({
  field,
  trigger,
  disabled,
  option,
  handleChange,
  placeholder,
  error,
  selectOptions,
  classes,
  labelId,
  disablePortal = true,
  menuPaperClassName,
  icon = IconName.CHEVRON_DOWN,
}: DropdownTextFieldProps<T>) => {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <TextField
      select
      disabled={disabled}
      className={`${classes.base} ${menuOpen ? 'Dropdown--expanded' : ''}`}
      SelectProps={{
        // biome-ignore lint/correctness/noNestedComponentDefinitions: closes over per-render props
        // biome-ignore lint/correctness/noComponentHookFactories: closes over per-render props
        IconComponent: (e) => <Icon name={icon} className={e.className} />,
        MenuProps: {
          disableScrollLock: false,
          keepMounted: true,
          disablePortal,
          className: classes.menu,
          transitionDuration: { enter: 200, exit: 150 },
          PaperProps: {
            className: menuPaperClassName,
            elevation: 8,
          },
          BackdropProps: {
            onMouseDown: (e) => {
              e.preventDefault();
            },
          },
        },
        displayEmpty: true,
        SelectDisplayProps: {
          'aria-labelledby': labelId,
        },
        renderValue: () =>
          option ? (
            option
          ) : (
            <span className={classes.placeholder}>{placeholder}</span>
          ),
        onOpen: () => setMenuOpen(true),
        onClose: () => setMenuOpen(false),
      }}
      value={(option || field?.value) ?? ''}
      onChange={(e) => {
        handleChange(e);
        if (field) {
          field.onChange(e);
          if (trigger) trigger(field.name);
        }
      }}
      onBlur={() => {
        setMenuOpen(false);
      }}
      inputProps={{
        'data-testid': 'select-input',
      }}
      error={!!error}
      helperText={error}
    >
      {selectOptions}
    </TextField>
  );
};

export default DropdownTextField;
